import { useState, useEffect } from 'react';
import { AlertCircle, Edit2, Trash2, Plus, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import { symptomsApi } from '../../lib/api/symptoms';
import { LoadingState } from '../common/LoadingState';
import { EmptyState } from '../common/EmptyState';
import { AlertCard } from '../common/AlertCard';
import { formatDate, formatTime } from '../common/utils';
import { PatientLayout } from './PatientLayout';
import React, { memo } from "react";


/* ---------------------------------------------------------------------
   MEMOIZED SLIDER FIELD
------------------------------------------------------------------------ */
const SymptomSliderField = memo(function SymptomSliderField({
  label,
  minText,
  maxText,
  value,
  onChange,
}: {
  label: string;
  minText: string;
  maxText: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-[#494949]">{label}</Label>

        <Badge variant="outline" className="text-[#2762AC]">
          {value}/10
        </Badge>
      </div>

      <Slider
        value={[value]}
        onValueChange={(val) => onChange(val[0])}
        max={10}
        step={1}
        className="w-full"
      />

      <div className="flex justify-between text-xs text-gray-500">
        <span>{minText}</span>
        <span>{maxText}</span>
      </div>
    </div>
  );
});

/* ---------------------------------------------------------------------
   MEMOIZED NOTES TEXTAREA
------------------------------------------------------------------------ */
const SymptomNotesField = memo(function SymptomNotesField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-[#494949]">Additional Notes (Optional)</Label>

      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Any additional information about your symptoms..."
        className="min-h-[100px]"
      />
    </div>
  );
});

/* ---------------------------------------------------------------------
   MAIN FORM (MEMOIZED)
------------------------------------------------------------------------ */
const SymptomFormFields = memo(function SymptomFormFields({
  formData,
  handleInputChange,
}: {
  formData: any;
  handleInputChange: (field: string, value: any) => void;
}) {
  return (
    <div className="space-y-6">

      <SymptomSliderField
        label="Breathlessness"
        minText="No symptoms"
        maxText="Very severe"
        value={formData.breathlessness}
        onChange={(val) => handleInputChange("breathlessness", val)}
      />

      <SymptomSliderField
        label="Energy Level"
        minText="Very low"
        maxText="Very high"
        value={formData.energy_level}
        onChange={(val) => handleInputChange("energy_level", val)}
      />

      <SymptomSliderField
        label="Cough"
        minText="No cough"
        maxText="Very severe"
        value={formData.cough}
        onChange={(val) => handleInputChange("cough", val)}
      />

      <SymptomSliderField
        label="Sleep Quality"
        minText="Very poor"
        maxText="Very good"
        value={formData.sleep_quality}
        onChange={(val) => handleInputChange("sleep_quality", val)}
      />

      <SymptomSliderField
        label="Chest Pain"
        minText="No pain"
        maxText="Very severe"
        value={formData.chest_pain}
        onChange={(val) => handleInputChange("chest_pain", val)}
      />

      <SymptomSliderField
        label="Fatigue"
        minText="No fatigue"
        maxText="Very severe"
        value={formData.fatigue}
        onChange={(val) => handleInputChange("fatigue", val)}
      />

      <SymptomNotesField
        value={formData.additional_notes}
        onChange={(v) => handleInputChange("additional_notes", v)}
      />

    </div>
  );
});


interface SymptomsManagementProps {
  patientId?: number;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function SymptomsManagement({ patientId, onNavigate, onLogout }: SymptomsManagementProps) {
  const [symptomsData, setSymptomsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState<any | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Fetch symptoms data
  const fetchSymptoms = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await symptomsApi.getMySymptomHistory(7);
      console.log('🩺 SymptomsManagement - API Response:', data);
      setSymptomsData(data?.symptoms || []);
    } catch (err: any) {
      console.error('❌ SymptomsManagement - Failed to fetch symptoms:', err);
      setError(err);
      setSymptomsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSymptoms();
  }, []);

  const refetch = fetchSymptoms;

  // Form data matching backend schema
  const [formData, setFormData] = useState({
    breathlessness: 0,
    energy_level: 10, // Higher is better (inverted in calculation)
    cough: 0,
    sleep_quality: 10, // Higher is better (inverted in calculation)
    chest_pain: 0,
    fatigue: 0,
    additional_notes: ''
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      breathlessness: 0,
      energy_level: 10,
      cough: 0,
      sleep_quality: 10,
      chest_pain: 0,
      fatigue: 0,
      additional_notes: ''
    });
  };

  const handleAdd = async () => {
    try {
      await symptomsApi.recordMySymptom({
        breathlessness: formData.breathlessness,
        energy_level: formData.energy_level,
        cough: formData.cough,
        sleep_quality: formData.sleep_quality,
        chest_pain: formData.chest_pain,
        fatigue: formData.fatigue,
        triggers: [],
        additional_notes: formData.additional_notes || undefined
      });

      toast.success('Symptoms logged successfully!');
      setIsAddDialogOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      console.error('Error logging symptoms:', error);
      toast.error(error?.message || 'Failed to log symptoms');
    }
  };

  const handleEdit = async () => {
    if (!editingSymptom) return;

    try {
      await symptomsApi.updateMySymptom(editingSymptom.id, {
        symptoms: {
          breathlessness: formData.breathlessness,
          energy_level: formData.energy_level,
          cough: formData.cough,
          sleep_quality: formData.sleep_quality,
          chest_pain: formData.chest_pain,
          fatigue: formData.fatigue,
        },
        additional_notes: formData.additional_notes
      });

      toast.success('Symptoms updated successfully!');
      setEditingSymptom(null);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update symptoms');
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      await symptomsApi.deleteMySymptom(deletingId);
      toast.success('Symptom log deleted successfully!');
      setDeletingId(null);
      refetch();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete symptom log');
    }
  };

  const openEditDialog = (symptom: any) => {
    setEditingSymptom(symptom);
    setFormData({
      breathlessness: symptom.symptoms.breathlessness || 0,
      energy_level: symptom.symptoms.energy_level || 10,
      cough: symptom.symptoms.cough || 0,
      sleep_quality: symptom.symptoms.sleep_quality || 10,
      chest_pain: symptom.symptoms.chest_pain || 0,
      fatigue: symptom.symptoms.fatigue || 0,
      additional_notes: symptom.additional_notes || ''
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'mild':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'significant':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'severe':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <PatientLayout currentView="symptoms" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading symptoms..." />
      </PatientLayout>
    );
  }

  return (
    <PatientLayout currentView="symptoms" onNavigate={onNavigate} onLogout={onLogout}>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[#494949]">My Symptoms</h1>
            <p className="text-sm text-gray-600">Last 7 days of symptom logs</p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#2762AC] hover:bg-[#1d4a7f] text-white">
                <Plus className="mr-2 h-4 w-4" />
                Log Symptoms
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[#2762AC]">Log Today's Symptoms</DialogTitle>
                <DialogDescription>
                  Rate your symptoms on a scale of 0-10
                </DialogDescription>
              </DialogHeader>
              <SymptomFormFields
                formData={formData}
                handleInputChange={handleInputChange}
              />

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button className="bg-[#2762AC] hover:bg-[#1d4a7f]" onClick={handleAdd}>
                  Save Symptoms
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Error State */}
        {error && (
          <AlertCard
            type="error"
            title="Error Loading Symptoms"
            description={error?.message || 'Failed to load symptoms'}
          />
        )}

        {/* Empty State */}
        {!error && symptomsData.length === 0 && (
          <EmptyState
            icon={Activity}
            title="No Symptoms Logged"
            description="Start tracking your symptoms to monitor your health"
            action={
              <Button 
                className="bg-[#2762AC] hover:bg-[#1d4a7f]"
                onClick={() => setIsAddDialogOpen(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Log First Symptom
              </Button>
            }
          />
        )}

        {/* Symptoms List */}
        {!error && symptomsData.length > 0 && (
          <div className="space-y-3">
            {symptomsData.map((symptom: any) => (
              <Card key={symptom.id} className="border-l-4 border-l-[#2762AC]">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Date and Time */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-[#494949]">
                          {formatDate(symptom.date)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTime(symptom.recorded_at)}
                        </span>
                        <Badge className={`${getSeverityColor(symptom.severity)}`}>
                          {symptom.severity}
                        </Badge>
                        <Badge variant="outline" className="text-[#2762AC] border-[#2762AC]">
                          Score: {symptom.overall_score.toFixed(1)}/10
                        </Badge>
                      </div>

                      {/* Symptom Details */}
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="text-sm">
                          <span className="text-gray-600">Breathlessness:</span>{' '}
                          <span className="text-[#494949]">{symptom.symptoms.breathlessness}/10</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Energy:</span>{' '}
                          <span className="text-[#494949]">{symptom.symptoms.energy_level}/10</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Cough:</span>{' '}
                          <span className="text-[#494949]">{symptom.symptoms.cough}/10</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Sleep:</span>{' '}
                          <span className="text-[#494949]">{symptom.symptoms.sleep_quality}/10</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Chest Pain:</span>{' '}
                          <span className="text-[#494949]">{symptom.symptoms.chest_pain}/10</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Fatigue:</span>{' '}
                          <span className="text-[#494949]">{symptom.symptoms.fatigue}/10</span>
                        </div>
                      </div>

                      {/* Notes */}
                      {symptom.additional_notes && (
                        <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700">
                          <span className="text-gray-600">Notes:</span> {symptom.additional_notes}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(symptom)}
                      >
                        <Edit2 className="h-4 w-4 text-[#2762AC]" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeletingId(symptom.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingSymptom} onOpenChange={(open) => !open && setEditingSymptom(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#2762AC]">Edit Symptoms</DialogTitle>
              <DialogDescription>
                Update your symptom ratings
              </DialogDescription>
            </DialogHeader>
            <SymptomFormFields />
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingSymptom(null)}>
                Cancel
              </Button>
              <Button className="bg-[#2762AC] hover:bg-[#1d4a7f]" onClick={handleEdit}>
                Update Symptoms
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Symptom Log?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this symptom log. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeletingId(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </PatientLayout>
  );
}

